import * as React from 'react';
import { useForm } from 'react-hook-form';

import {
  BarcodeDisplayOptions,
  BarcodeTypes,
  LabelContent,
  LabelData,
  LabelMargins,
  LabelSize,
  LabelTemplate,
} from 'label/utils/types';
import { getOptions } from 'pages/views/devices/utils/options';
import Format from 'root/apps/app/src/data/Format';
import { getItemPrintedTimestamp } from 'root/apps/label/utils/timeStampHelpers';

import { getLabelQuantity } from '../HubLabelPrintModal';
import { useLocalStorage } from '.';

interface FieldArray {
  name:
    | 'fieldArray'
    | 'quantity.labels'
    | 'default'
    | 'labelMargins.small.left'
    | 'labelMargins.normal.left'
    | 'labelMargins.alt.left'
    | 'labelMargins.jewelry.left'
    | 'labelMargins.small.top'
    | 'labelMargins.normal.top'
    | 'labelMargins.alt.top'
    | 'labelMargins.jewelry.top';
  value: number;
  id: number;
}

export function useLabelPrintForm({
  labelData,
  isLoading,
}: {
  labelData: LabelData[] | undefined;
  isLoading: boolean;
}) {
  const [previousLabelSize, setPreviousLabelSize] = useLocalStorage<LabelSize | undefined>(
    'LABEL_SIZE',
    undefined
  );

  const [previousLabelMarginsAlt, setPreviousLabelMarginAlt] = useLocalStorage(
    'LABEL_MARGIN_ALT_LEFT',
    0
  );

  const [previousLabelMarginsJewelry, setPreviousLabelMarginJewelry] = useLocalStorage(
    'LABEL_MARGIN_JEWELRY_LEFT',
    0
  );

  const [previousLabelMarginsSmall, setPreviousLabelMarginSmall] = useLocalStorage(
    'LABEL_MARGIN_SMALL_LEFT',
    0
  );

  const [previousLabelMarginsNormal, setPreviousLabelMarginNormal] = useLocalStorage(
    'LABEL_MARGIN_NORMAL_LEFT',
    0
  );

  const [previousLabelMarginsTopAlt, setPreviousLabelMarginTopAlt] = useLocalStorage(
    'LABEL_MARGIN_ALT_TOP',
    0
  );

  const [previousLabelMarginsTopJewelry, setPreviousLabelMarginTopJewelry] = useLocalStorage(
    'LABEL_MARGIN_JEWELRY_TOP',
    0
  );

  const [previousLabelMarginsTopSmall, setPreviousLabelMarginTopSmall] = useLocalStorage(
    'LABEL_MARGIN_SMALL_TOP',
    0
  );

  const [previousLabelMarginsTopNormal, setPreviousLabelMarginTopNormal] = useLocalStorage(
    'LABEL_MARGIN_NORMAL_TOP',
    0
  );

  const [previousDisplayPrice, setPreviousDisplayPrice] = useLocalStorage(
    'LABEL_DISPLAY_PRICE',
    true
  );

  const [previousLabelTemplate, setPreviousLabelTemplate] = useLocalStorage<
    LabelTemplate | undefined
  >('LABEL_TEMPLATE', undefined);

  const [previousLabelContent, setPreviousLabelContent] = useLocalStorage<LabelContent | undefined>(
    'LABEL_CONTENT',
    undefined
  );

  const [previousLabelContentDefault, setPreviousLabelContentDefault] = useLocalStorage<
    LabelContent | undefined
  >('LABEL_CONTENT_DEFAULT', undefined);

  const [dateNow, setDateNow] = React.useState<string | undefined>();

  const [previousBarcodeDisplayOption, setPreviousBarcodeDisplayOption] = useLocalStorage(
    'LABEL_BARCODE_DISPLAY_OPTION',
    BarcodeDisplayOptions.BARCODE_AND_NUMBERS
  );

  const [previousBarcodeType, setPreviousBarcodeType] = useLocalStorage(
    'LABEL_BARCODE_TYPE',
    BarcodeTypes.SYSTEM_SKU
  );

  const setPreviousLabelMargin = (labelMargins: LabelMargins) => {
    setPreviousLabelMarginAlt(labelMargins.alt.left);
    setPreviousLabelMarginTopAlt(labelMargins.alt.top);

    setPreviousLabelMarginJewelry(labelMargins.jewelry.left);
    setPreviousLabelMarginTopJewelry(labelMargins.jewelry.top);

    setPreviousLabelMarginNormal(labelMargins.normal.left);
    setPreviousLabelMarginTopNormal(labelMargins.normal.top);

    setPreviousLabelMarginSmall(labelMargins.small.left);
    setPreviousLabelMarginTopSmall(labelMargins.small.top);
  };

  const { control, watch, register, setValue, formState, handleSubmit, getValues } = useForm<{
    fieldArray: FieldArray[];
    labelQuantity: number;
    quantity: { labels: number };
    labelSize: LabelSize;
    labelMargins: LabelMargins;
    default: undefined;
    displayPrice: boolean;
    barcodeDisplayOption: BarcodeDisplayOptions;
    barcodeType: BarcodeTypes;
    labelContent?: LabelContent;
    labelContentDefault?: LabelContent;
    labelTemplate: LabelTemplate | undefined;
  }>({
    defaultValues: {
      labelQuantity: 1,
      quantity: {
        labels: 1,
      },
      labelSize: previousLabelSize,
      labelMargins: {
        alt: {
          top: previousLabelMarginsTopAlt,
          left: previousLabelMarginsAlt,
        },
        jewelry: {
          top: previousLabelMarginsTopJewelry,
          left: previousLabelMarginsJewelry,
        },
        normal: {
          top: previousLabelMarginsTopNormal,
          left: previousLabelMarginsNormal,
        },
        small: {
          top: previousLabelMarginsTopSmall,
          left: previousLabelMarginsSmall,
        },
      },
      displayPrice: previousDisplayPrice,
      barcodeDisplayOption: previousBarcodeDisplayOption,
      barcodeType: previousBarcodeType,
      labelContent: previousLabelContent,
      labelContentDefault: previousLabelContentDefault,
      labelTemplate: previousLabelTemplate,
      fieldArray: [
        { name: 'default', value: 0, id: 1 },
        { name: 'quantity.labels', value: 1, id: 2 },
        { name: 'labelMargins.jewelry.left', value: 1, id: 3 },
        { name: 'labelMargins.small.left', value: 1, id: 4 },
        { name: 'labelMargins.normal.left', value: 1, id: 5 },
        { name: 'labelMargins.alt.left', value: 1, id: 6 },
        { name: 'labelMargins.jewelry.top', value: 1, id: 7 },
        { name: 'labelMargins.small.top', value: 1, id: 8 },
        { name: 'labelMargins.normal.top', value: 1, id: 9 },
        { name: 'labelMargins.alt.top', value: 1, id: 10 },
      ],
    },
    mode: 'onChange',
  });

  const {
    labelQuantity,
    labelSize,
    labelMargins,
    fieldArray,
    quantity,
    displayPrice,
    barcodeDisplayOption,
    barcodeType,
    labelContent,
    labelContentDefault,
    labelTemplate,
  } = watch();

  const setLabelContentDefault = (value: LabelContent) => {
    setValue('labelContentDefault', value);
    setPreviousLabelContentDefault(value);
  };

  const getIsMsrpSet = async () => {
    const options = getOptions();
    const { accountId, shopId } = options;

    const res = await fetch(`/API/Account/${accountId}/Shop/${shopId}.json`)
      .then((response) => {
        return response.json();
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Error retrieving shop data', err);
      });

    const {
      Shop: { labelMsrp },
    } = res;

    const labelMsrpAccountLevelSetting = Format.parseBoolean(labelMsrp, false);

    labelMsrpAccountLevelSetting
      ? setLabelContentDefault(LabelContent.MSRP)
      : setLabelContentDefault(LabelContent.NONE);

    if (labelContent === null && labelMsrpAccountLevelSetting) {
      setValue('labelContent', LabelContent.MSRP);
      setPreviousLabelContent(LabelContent.MSRP);
    }

    if (labelContent === null && !labelMsrpAccountLevelSetting) {
      setValue('labelContent', LabelContent.NONE);
      setPreviousLabelContent(LabelContent.NONE);
    }

    return labelMsrpAccountLevelSetting;
  };

  const getIsMarginSet = () => {
    const labelSizes = Object.keys(LabelSize);
    const labelMarginSetFunctionsByLabelSize = {
      [LabelSize.NORMAL]: {
        setFunction: setPreviousLabelMarginNormal,
      },
      [LabelSize.ALT]: {
        setFunction: setPreviousLabelMarginAlt,
      },
      [LabelSize.SMALL]: {
        setFunction: setPreviousLabelMarginSmall,
      },
      [LabelSize.JEWELRY]: {
        setFunction: setPreviousLabelMarginJewelry,
      },
    };

    labelSizes.forEach((labelSize) => {
      const labelMarginKey = `LABEL_MARGIN_${labelSize.toUpperCase()}`;
      const previousLabelMargin = parseInt(localStorage.getItem(labelMarginKey) as string, 10);

      labelMarginSetFunctionsByLabelSize[labelSize.toLowerCase() as LabelSize].setFunction(
        previousLabelMargin
      );
      localStorage.removeItem(labelMarginKey);
    });
  };

  React.useEffect(() => {
    getIsMsrpSet();

    const updateTimestamp = async () => {
      setDateNow(await getItemPrintedTimestamp());
    };

    updateTimestamp();
    getIsMarginSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setValue('quantity.labels', getLabelQuantity(labelData));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelData]);

  React.useEffect(() => {
    setValue('labelQuantity', quantity.labels || 1);
  }, [quantity.labels, setValue]);

  React.useEffect(() => {
    setValue('fieldArray', fieldArray);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelData, isLoading, setValue]);

  return {
    control,
    displayPrice,
    setPreviousDisplayPrice,
    barcodeDisplayOption,
    previousBarcodeDisplayOption,
    setPreviousBarcodeDisplayOption,
    barcodeType,
    setPreviousBarcodeType,
    labelContent,
    labelContentDefault,
    setPreviousLabelContent,
    dateNow,
    labelQuantity,
    quantity,
    labelSize,
    getValues,
    previousLabelSize,
    setPreviousLabelSize,
    labelMargins,
    formState,
    setValue,
    register,
    setPreviousLabelMargin,
    handleSubmit,
    watch,
    fieldArray,
    previousLabelTemplate,
    setPreviousLabelTemplate,
    labelTemplate,
  };
}
